// Google Analytics utility functions
// This file provides helper functions for tracking events and page views

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface PageViewEvent {
  page_title?: string;
  page_location?: string;
  page_path?: string;
}

// Check if Google Analytics is loaded
export const isGoogleAnalyticsLoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track page views
export const trackPageView = (event: PageViewEvent = {}): void => {
  if (!isGoogleAnalyticsLoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  try {
    window.gtag('config', 'G-51ZFNXKW3R', {
      page_title: event.page_title || document.title,
      page_location: event.page_location || window.location.href,
      page_path: event.page_path || window.location.pathname,
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = (event: AnalyticsEvent): void => {
  if (!isGoogleAnalyticsLoaded()) {
    console.warn('Google Analytics not loaded');
    return;
  }

  try {
    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.custom_parameters,
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Predefined event tracking functions for common actions
export const analytics = {
  // Newsletter signup tracking
  trackNewsletterSignup: (provider: string = 'emailoctopus') => {
    trackEvent({
      action: 'newsletter_signup',
      category: 'engagement',
      label: provider,
      value: 1,
    });
  },

  // Contact form submission
  trackContactForm: (formType: string = 'contact') => {
    trackEvent({
      action: 'form_submission',
      category: 'lead_generation',
      label: formType,
      value: 1,
    });
  },

  // Get started form submission
  trackGetStarted: () => {
    trackEvent({
      action: 'get_started_form',
      category: 'lead_generation',
      label: 'project_inquiry',
      value: 1,
    });
  },

  // Portfolio case study views
  trackPortfolioView: (caseStudy: string) => {
    trackEvent({
      action: 'portfolio_view',
      category: 'engagement',
      label: caseStudy,
    });
  },

  // Blog post views
  trackBlogView: (postTitle: string) => {
    trackEvent({
      action: 'blog_view',
      category: 'content',
      label: postTitle,
    });
  },

  // Service page views
  trackServiceView: (service: string) => {
    trackEvent({
      action: 'service_view',
      category: 'engagement',
      label: service,
    });
  },

  // Phone call clicks
  trackPhoneCall: () => {
    trackEvent({
      action: 'phone_call',
      category: 'lead_generation',
      label: 'header_phone',
      value: 1,
    });
  },

  // Email clicks
  trackEmailClick: () => {
    trackEvent({
      action: 'email_click',
      category: 'lead_generation',
      label: 'contact_email',
      value: 1,
    });
  },

  // Social media clicks
  trackSocialClick: (platform: string) => {
    trackEvent({
      action: 'social_click',
      category: 'engagement',
      label: platform,
    });
  },

  // Download tracking (for resources, PDFs, etc.)
  trackDownload: (fileName: string) => {
    trackEvent({
      action: 'download',
      category: 'engagement',
      label: fileName,
    });
  },

  // External link clicks
  trackExternalLink: (url: string) => {
    trackEvent({
      action: 'external_link',
      category: 'engagement',
      label: url,
    });
  },

  // Search usage
  trackSearch: (searchTerm: string) => {
    trackEvent({
      action: 'search',
      category: 'engagement',
      label: searchTerm,
    });
  },

  // Video interactions
  trackVideoPlay: (videoTitle: string) => {
    trackEvent({
      action: 'video_play',
      category: 'engagement',
      label: videoTitle,
    });
  },

  // Scroll depth tracking
  trackScrollDepth: (percentage: number) => {
    trackEvent({
      action: 'scroll_depth',
      category: 'engagement',
      label: `${percentage}%`,
      value: percentage,
    });
  },

  // Time on page tracking
  trackTimeOnPage: (seconds: number, page: string) => {
    trackEvent({
      action: 'time_on_page',
      category: 'engagement',
      label: page,
      value: seconds,
    });
  },
};